import React from "react";
import { Alert, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useSuperAdminMetrics } from "../hooks";

// Recharts chosen over react-chartjs-2 because it is a pure-React library with
// no imperative canvas API, simpler SSR/test compatibility, and covers all cases
// needed here (line + bar). react-chartjs-2 would require additional Chart.js
// registration boilerplate; recharts is self-contained.

// `mrr` y `arpa` llegan como string decimal ("1580000.00"): el backend los
// serializa con str(Decimal) para no perder precisión.
function formatCOP(amount) {
  const n = Number(amount);
  if (amount == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    notation: "compact",
  }).format(n);
}

// Los porcentajes del backend YA vienen en escala 0-100 (churn_pct_30d,
// nrr_proxy, activation_pct_14d) — no se multiplican otra vez.
function formatPct(val) {
  if (val == null) return "—";
  return `${Number(val).toFixed(1)}%`;
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ title, value, description, variant = "primary" }) {
  return (
    <Card className={`border-${variant} h-100`}>
      <Card.Body>
        <div className="text-muted small mb-1">{title}</div>
        <div className={`fs-4 fw-bold text-${variant}`}>{value}</div>
        {description && (
          <div className="text-muted small mt-1">{description}</div>
        )}
      </Card.Body>
    </Card>
  );
}

// ── MetricsPage ───────────────────────────────────────────────────────────────

function MetricsPage() {
  const { data: metrics, isLoading, isError } = useSuperAdminMetrics();

  if (isLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner aria-label="Cargando métricas" />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container className="py-4">
        <Alert variant="danger">No se pudo cargar las métricas del negocio.</Alert>
      </Container>
    );
  }

  // Nombres reales del payload de /superadmin/metrics/ (apps/saas/views.py
  // ::SuperAdminMetricsView._compute_metrics).
  const {
    mrr,
    arpa,
    churn_pct_30d: churn,
    nrr_proxy: nrr,
    activation_pct_14d: activation,
    active_subscriptions: activeSubs,
    total_subscriptions: totalSubs,
    total_companies: totalCompanies,
  } = metrics ?? {};

  // TASK-20: el backend RECORTA el payload según el rol de staff
  // (METRICS_FIELDS_BY_ROLE) — FINANCE no recibe activación, SALES no recibe
  // mrr/arpa/churn/nrr, SUPPORT recibe 403 y no llega aquí. Renderizamos por
  // PRESENCIA del campo y no con can(): la matriz solo tiene `metrics:read` y
  // no distingue campos, así que el payload es la única señal veraz.
  const has = (v) => v != null;

  // Embudo de activación (trial → pago). Solo dos puntos reales: las academias
  // que inician trial (100%) y las que se activan. La conversión a pago no la
  // calcula el backend, así que no se inventa.
  const activationData = [
    { name: "Trial iniciado", value: 100 },
    { name: "Activado (≥1 docente activo)", value: Math.round(activation ?? 0) },
  ];

  return (
    <Container fluid className="py-4" style={{ maxWidth: "1100px" }}>
      <h4 className="mb-4">Métricas del negocio</h4>

      {/* ── KPI Cards ── */}
      <Row className="g-3 mb-4">
        {has(mrr) && (
          <Col xs={6} md={3}>
            <KpiCard
              title="MRR"
              value={formatCOP(mrr)}
              description="Ingreso recurrente mensual"
              variant="success"
            />
          </Col>
        )}
        {has(arpa) && (
          <Col xs={6} md={3}>
            <KpiCard
              title="ARPA"
              value={formatCOP(arpa)}
              description="Ingreso medio por academia"
              variant="primary"
            />
          </Col>
        )}
        {has(churn) && (
          <Col xs={6} md={3}>
            <KpiCard
              title="Churn"
              value={formatPct(churn)}
              description="% academias que cancelan/mes"
              variant={churn > 4 ? "danger" : "warning"}
            />
          </Col>
        )}
        {has(nrr) && (
          <Col xs={6} md={3}>
            <KpiCard
              title="NRR"
              value={formatPct(nrr)}
              description="Net Revenue Retention"
              variant={nrr >= 100 ? "success" : "warning"}
            />
          </Col>
        )}
        {has(activation) && (
          <Col xs={6} md={3}>
            <KpiCard
              title="Activación (14 d)"
              value={formatPct(activation)}
              description="% academias activadas"
              variant="info"
            />
          </Col>
        )}
      </Row>

      {/* ── Volumen (visible para todos los roles con acceso a métricas) ── */}
      <Row className="g-3 mb-4">
        {has(totalCompanies) && (
          <Col xs={6} md={3}>
            <KpiCard title="Academias" value={totalCompanies} description="Total registradas" variant="secondary" />
          </Col>
        )}
        {has(activeSubs) && (
          <Col xs={6} md={3}>
            <KpiCard title="Suscripciones activas" value={activeSubs} description="En estado ACTIVE" variant="secondary" />
          </Col>
        )}
        {has(totalSubs) && (
          <Col xs={6} md={3}>
            <KpiCard title="Suscripciones" value={totalSubs} description="Total histórico" variant="secondary" />
          </Col>
        )}
      </Row>

      {/* ── Activación trial → pago (bar chart) ── */}
      {has(activation) && (
        <Card>
          <Card.Header>
            <strong>Embudo de activación (trial → activación)</strong>
          </Card.Header>
          <Card.Body>
            <div role="img" aria-label="Gráfico de barras del embudo de activación de trial a activación">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={activationData}
                  margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => [`${value}%`, "Porcentaje"]} />
                  <Bar dataKey="value" fill="#198754" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-muted small mt-2 mb-0">
              Tasa de activación: {formatPct(activation)} — objetivo: &gt; 20–30%
              (ESTRATEGIA §10)
            </p>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export { MetricsPage };
